import { adoptPrimeIconsStyles, loadBaseCSS } from '../shared/button-utils.js';
import { upgradeProperty } from '../shared/form-control.js';
import { parseQdsIcon, renderQdsIcon } from '../shared/icon-utils.js';
import { primitiveStyles } from '../shared/primitive-utils.js';

/**
 * Decorative or labelled graphic, using shared PrimeIcons styles or a slotted native SVG.
 * @element qds-icon
 * @attr {string} name - Safe PrimeIcons name, e.g. prime:pi-briefcase or pi-check.
 * @attr {string} label - Nonempty label exposes an image role; omitted means decorative.
 * @property {string} name - PrimeIcons glyph; other names use the custom graphic slot.
 * @property {string} label - Accessible image name, or empty for decorative graphics.
 * @slot - Custom native SVG/graphic when name is omitted or not a PrimeIcons name.
 * @csspart icon - Named glyph or custom-graphic slot.
 */
export class QdsIcon extends HTMLElement {
  static get observedAttributes(): string[] { return ['name', 'label']; }
  constructor() {
    super();
    this.attachShadow({ mode: 'open' }).innerHTML = `<style>${primitiveStyles}
      :host { display: inline-flex; align-items: center; justify-content: center; vertical-align: -.125em;
        font-size: var(--qds-icon-size, 20px); line-height: 1; flex: 0 0 auto; }
      .graphic { display: inline-flex; align-items: center; justify-content: center; width: 1em; height: 1em; }
      .pi { font-size: inherit; line-height: 1; }
      ::slotted(svg), ::slotted(img) { width: 1em; height: 1em; }
    </style><span class="graphic" aria-hidden="true"><span class="glyph"></span><slot part="icon"></slot></span>`;
    loadBaseCSS();
  }
  connectedCallback(): void { upgradeProperty(this, 'name'); upgradeProperty(this, 'label'); this._sync(); }
  attributeChangedCallback(): void { if (this.isConnected) this._sync(); }
  get name(): string { return this.getAttribute('name') || ''; }
  set name(value: string) { this.setAttribute('name', value ?? ''); }
  get label(): string { return this.getAttribute('label') || ''; }
  set label(value: string) { this.setAttribute('label', value ?? ''); }
  private _sync(): void {
    if (this.label.trim()) { this.setAttribute('role', 'img'); this.setAttribute('aria-label', this.label.trim()); this.removeAttribute('aria-hidden'); }
    else { this.removeAttribute('role'); this.removeAttribute('aria-label'); this.setAttribute('aria-hidden', 'true'); }
    const icon = parseQdsIcon(this.name);
    const markup = icon?.library === 'prime' ? renderQdsIcon(this.name, '', 'icon') : '';
    const glyph = this.shadowRoot!.querySelector<HTMLElement>('.glyph')!; glyph.innerHTML = markup; glyph.hidden = !markup;
    this.shadowRoot!.querySelector<HTMLSlotElement>('slot')!.hidden = !!markup;
    if (markup) void adoptPrimeIconsStyles(this.shadowRoot!);
  }
}
if (!customElements.get('qds-icon')) customElements.define('qds-icon', QdsIcon);
